/** @format **/

/**
 * External dependencies
 */
import { __ } from '@wordpress/i18n';
import React, { useContext, useRef, useState } from 'react';
import apiFetch from '@wordpress/api-fetch';

/**
 * Internal dependencies
 */
import {
	Button,
	Notice,
	TextareaControl,
	TextControl,
} from '@wordpress/components';
import CollapsibleBody from 'wcpay/components/wizard/collapsible-body';
import WizardTaskItem from 'wcpay/components/wizard/task-item';
import WizardTaskContext from 'wcpay/components/wizard/task/context';
import {
	VatError,
	VatFormOnCompleted,
	VatSaveDetails,
	VatSaveDetailsResult,
} from '../../types';

export const CompanyDataTask = ( {
	onCompleted,
	vatNumber,
	placeholderCompanyName,
	placeholderCompanyAddress,
}: {
	onCompleted: VatFormOnCompleted;
	vatNumber: string | null;
	placeholderCompanyName: string;
	placeholderCompanyAddress: string;
} ): JSX.Element => {
	const { setCompleted } = useContext( WizardTaskContext );

	const [ saveDetailsError, setSaveDetailsError ] = useState< string | null >(
		null
	);
	const [ isLoading, setLoading ] = useState< boolean >( false );

	const [ companyName, setCompanyName ] = useState< string >(
		placeholderCompanyName
	);
	const [ companyAddress, setCompanyAddress ] = useState< string >(
		placeholderCompanyAddress
	);

	// Reset local drafts when the VAT lookup props change (parent updates
	// placeholders after the VAT-number step). Render-time state adjustment
	// avoids a cascading render from a sync effect.
	const prevPlaceholdersRef = useRef( {
		name: placeholderCompanyName,
		address: placeholderCompanyAddress,
	} );
	if (
		prevPlaceholdersRef.current.name !== placeholderCompanyName ||
		prevPlaceholdersRef.current.address !== placeholderCompanyAddress
	) {
		prevPlaceholdersRef.current = {
			name: placeholderCompanyName,
			address: placeholderCompanyAddress,
		};
		setCompanyName( placeholderCompanyName );
		setCompanyAddress( placeholderCompanyAddress );
	}

	const isConfirmButtonDisabled =
		companyName.trim() === '' || companyAddress.trim() === '';

	const submit = async () => {
		try {
			setLoading( true );

			const details: VatSaveDetails = {
				name: companyName,
				address: companyAddress,
			};

			if ( vatNumber !== null ) {
				details.vat_number = vatNumber;
			}

			const savedDetails = await apiFetch< VatSaveDetailsResult >( {
				path: '/wc/v3/payments/vat',
				method: 'POST',
				data: details,
			} );

			setLoading( false );

			setCompleted( true, 'vat-submitted' );
			onCompleted(
				savedDetails.vat_number,
				savedDetails.name,
				savedDetails.address
			);
		} catch ( error ) {
			setLoading( false );
			setSaveDetailsError( ( error as VatError ).message );
		}
	};

	return (
		<WizardTaskItem
			index={ 2 }
			title={ __(
				'Confirm your business details',
				'woocommerce-payments'
			) }
		>
			<CollapsibleBody>
				<TextControl
					className="wcpay-company-data-task__text-control"
					label={ __( 'Business name', 'woocommerce-payments' ) }
					value={ companyName }
					onChange={ setCompanyName }
					__nextHasNoMarginBottom
					__next40pxDefaultSize
				/>

				<TextareaControl
					className="wcpay-company-data-task__textarea-control"
					label={ __( 'Address', 'woocommerce-payments' ) }
					value={ companyAddress }
					onChange={ setCompanyAddress }
					__nextHasNoMarginBottom
				/>

				<Button
					variant="primary"
					disabled={ isConfirmButtonDisabled || isLoading }
					isBusy={ isLoading }
					onClick={ submit }
					__next40pxDefaultSize
				>
					{ __( 'Confirm', 'woocommerce-payments' ) }
				</Button>

				{ saveDetailsError && (
					<Notice
						status="error"
						isDismissible={ false }
						className="vat-number-error"
					>
						{ saveDetailsError }
					</Notice>
				) }
			</CollapsibleBody>
		</WizardTaskItem>
	);
};
